/**
 * BasicSettingsForm - Basic dataset settings form
 * @module components/starnexus/wizard/forms/BasicSettingsForm
 *
 */

import React, { useState, useEffect } from 'react';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Select from '@amzn/awsui-components-console/select';
import Textarea from '@amzn/awsui-components-console/textarea';
import Checkbox from '@amzn/awsui-components-console/checkbox';
import Table from '@amzn/awsui-components-console/table';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import { useAuthStore } from '../../../../stores/starnexus/authStore';
import type { BasicSettings, ProcessingType, SchemaColumn, DataType } from '../../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

/** Map verbose AI types to short display names */
const typeLabel = (t: string) => {
  const map: Record<string, string> = { VARCHAR: 'string', INTEGER: 'int', DECIMAL: 'decimal', BOOLEAN: 'bool', DATE: 'date', TIMESTAMP: 'timestamp', INT: 'int' };
  return map[t.toUpperCase()] || t.toLowerCase();
};

/** Data type options for the editable schema table dropdown */
const DATA_TYPE_OPTIONS: Array<{ value: DataType; label: string }> = [
  { value: 'string', label: 'string' },
  { value: 'integer', label: 'integer' },
  { value: 'decimal', label: 'decimal' },
  { value: 'boolean', label: 'boolean' },
  { value: 'timestamp', label: 'timestamp' },
];

interface BasicSettingsFormProps {
  settings: BasicSettings;
  onChange: (settings: BasicSettings) => void;
  schema?: SchemaColumn[];
  onSchemaChange?: (schema: SchemaColumn[]) => void;
  filePreview?: string[][] | null;
  /**
   * Solution A: schema is locked once stepAndes succeeds because the Andes table
   * has been created with these column types and cannot be retroactively changed.
   * When true, the data type Select / nullable checkbox / description input become
   * read-only and a banner explains why.
   */
  isSchemaLocked?: boolean;
}

// Hardcoded IVT teams - expand as we onboard more orgs
const FALLBACK_TEAMS = [
  { value: 'ivt-starcatcher', label: 'IVT - Starcatcher (Analytics)' },
  { value: 'ivt-starbridge', label: 'IVT - StarBridge (CDS)' },
  { value: 'ivt-starmailer', label: 'IVT - StarMailer (ETL)' },
  { value: 'ivt-operations', label: 'IVT - Operations' },
  { value: 'ivt-product', label: 'IVT - Product' },
  { value: 'other', label: 'Other (contact admin)' },
];

const TEAM_OPTIONS_DEFAULT = FALLBACK_TEAMS;

const PROCESSING_TYPE_OPTIONS = [
  {
    value: 'csv-to-json',
    label: 'CSV to JSON',
    description: 'Convert CSV files to JSON format'
  },
  {
    value: 'json-passthrough',
    label: 'JSON Passthrough',
    description: 'Pass JSON files through with optional transformation'
  },
  {
    value: 'excel-to-json',
    label: 'Excel to JSON',
    description: 'Convert Excel files to JSON format'
  }
];

// Dataset name allows hyphens (business identifier). Andes table name auto-converts - to _.
const DATASET_NAME_PATTERN = /^[a-z][a-z0-9_-]*[a-z0-9]$/;
const validateDatasetName = (name: string): string | null => {
  if (!name || name.trim().length === 0) return 'Dataset name is required';
  if (name.length < 2) return 'Dataset name must be at least 2 characters';
  if (name.length > 255) return 'Dataset name cannot exceed 255 characters';
  if (!DATASET_NAME_PATTERN.test(name)) {
    if (/^[0-9]/.test(name)) return 'Dataset name must start with a letter';
    if (/^[-_]/.test(name)) return 'Dataset name must start with a letter';
    if (/[-_]$/.test(name)) return 'Dataset name cannot end with a hyphen or underscore';
    if (/[A-Z]/.test(name)) return 'Dataset name must be lowercase';
    return 'Dataset name: lowercase letters, digits, hyphens, and underscores only';
  }
  return null;
};

export const BasicSettingsForm: React.FC<BasicSettingsFormProps> = ({ settings, onChange, schema, onSchemaChange, filePreview, isSchemaLocked = false }) => {
  const alias = useAuthStore((state) => state.alias) || 'unknown';
  const [teamOptions, setTeamOptions] = useState(TEAM_OPTIONS_DEFAULT);

  // Load teams the current user belongs to. Auto-bootstraps {alias}-personal if user has no team yet.
  useEffect(() => {
    apiFetch(apiUrl(`/api/team/teams/mine?alias=${encodeURIComponent(alias)}`), { ...apiFetchDefaults })
      .then(r => r.json())
      .then(data => {
        const teams = Array.isArray(data.teams) ? data.teams : [];
        if (teams.length > 0) {
          setTeamOptions(
            teams.map((t: any) => ({
              value: t.teamId,
              label: t.teamName || t.teamId,
              description: t.defaultProviderName ? `Default provider: ${t.defaultProviderName}` : undefined,
            }))
          );
          // Auto-select first team if nothing selected yet
          if (!settings.teamId && teams[0]?.teamId) {
            onChange({ ...settings, teamId: teams[0].teamId });
          }
        }
      })
      .catch(err => {
        // Non-fatal: fall back to empty team list, user can retry via Settings.
        console.warn('[BasicSettingsForm] Failed to load user teams:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  // When the wizard navigates back to this form from a later step, the form
  // remounts with touched={}, which would hide errors even though the user
  // has "been here before". Detect this by presence of any schema column (which
  // is populated by AI after step 2) and force-show validation errors.
  const hasProgressedBeyondStep1 = (schema?.length ?? 0) > 0;
  const showAllErrors = hasProgressedBeyondStep1;
  const nameError = (touched.datasetName || showAllErrors) ? validateDatasetName(settings.datasetName || '') : null;
  const teamError = (touched.teamId || showAllErrors) && !settings.teamId ? 'Team is required' : null;

  // Soft conflict probe: same team already has a dataset with this display name.
  // Debounced blur-based check to avoid API spam on every keystroke.
  const [nameConflict, setNameConflict] = useState<{ count: number; sample: string } | null>(null);

  const probeNameConflict = React.useCallback(async () => {
    const ds = (settings.datasetName || '').trim();
    const tm = (settings.teamId || '').trim();
    if (!ds || !tm) { setNameConflict(null); return; }
    if (validateDatasetName(ds)) { setNameConflict(null); return; }
    try {
      const r = await apiFetch(apiUrl(`/api/datasets/check-conflict?teamId=${encodeURIComponent(tm)}&datasetName=${encodeURIComponent(ds)}`), { ...apiFetchDefaults });
      if (r.ok) {
        const data = await r.json();
        if (data.exists && data.count > 0) {
          const sample = data.datasets?.[0]?.providerName
            ? `under provider "${data.datasets[0].providerName}"`
            : 'elsewhere';
          setNameConflict({ count: data.count, sample });
        } else {
          setNameConflict(null);
        }
      }
    } catch {
      setNameConflict(null);
    }
  }, [settings.datasetName, settings.teamId]);

  const handleChange = (field: keyof BasicSettings, value: string) => {
    setTouched(t => ({ ...t, [field]: true }));
    onChange({ ...settings, [field]: value });
  };

  return (
    <SpaceBetween size="l">
      <FormField
        label={<span>Dataset name <span style={{ color: '#d91515' }}>*</span></span>}
        description="A unique identifier for this dataset"
        constraintText="Lowercase letters, numbers, underscores, and hyphens. Must start with a letter."
        errorText={nameError || undefined}
        warningText={!nameError && nameConflict
          ? `Your team already has ${nameConflict.count} dataset${nameConflict.count > 1 ? 's' : ''} named "${settings.datasetName}" ${nameConflict.sample}. Not blocking, but consider a unique name.`
          : undefined}
      >
        <Input
          value={settings.datasetName}
          onChange={({ detail }) => handleChange('datasetName', detail.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
          onBlur={() => probeNameConflict()}
          placeholder="my-dataset-name"
          ariaLabel="Dataset name input"
          ariaRequired={true}
        />
      </FormField>

      <FormField
        label={<span>Team <span style={{ color: '#d91515' }}>*</span></span>}
        description="The team that owns this dataset"
        errorText={teamError || undefined}
      >
        <Select
          selectedOption={teamOptions.find((o) => o.value === settings.teamId) || (settings.teamId ? { value: settings.teamId, label: settings.teamId } : null)}
          onChange={({ detail }) => handleChange('teamId', detail.selectedOption.value || '')}
          options={teamOptions}
          placeholder={teamOptions.length === 0 || teamOptions[0]?.label === 'Loading teams...' ? 'Loading teams...' : 'Select a team'}
          ariaLabel="Select team that owns this dataset"
          ariaRequired={true}
        />
      </FormField>

      <FormField label="Description" description="A brief description of the dataset and its purpose">
        <Textarea
          value={settings.description}
          onChange={({ detail }) => handleChange('description', detail.value)}
          placeholder="Enter a description for this dataset..."
          rows={3}
          ariaLabel="Dataset description"
        />
      </FormField>

      <FormField label="Processing type" description="How the data should be processed">
        <Select
          selectedOption={PROCESSING_TYPE_OPTIONS.find((o) => o.value === settings.processingType) || PROCESSING_TYPE_OPTIONS[0]}
          onChange={({ detail }) => handleChange('processingType', detail.selectedOption.value as ProcessingType)}
          options={PROCESSING_TYPE_OPTIONS}
          ariaLabel="Select data processing type"
        />
      </FormField>

      {/* Source Schema (editable: dataType, nullable, doc; read-only: columnName).
          Solution A: once stepAndes locks the Andes table, all editable fields become
          read-only and an info banner explains. */}
      {schema && schema.length > 0 && (
        <ExpandableSection headerText={`Schema (${schema.length} columns)`} defaultExpanded variant="footer">
          <SpaceBetween size="s">
            {isSchemaLocked && (
              <Alert type="info" header="Schema locked">
                The Andes table has been created with these column types. Schema cannot be changed.
                Create a new dataset to use different column types.
              </Alert>
            )}
            <Table
              columnDefinitions={[
                {
                  id: 'col',
                  header: 'Column name',
                  cell: (item: SchemaColumn) => <Box fontWeight="bold">{item.columnName}</Box>,
                  width: 180,
                },
                {
                  id: 'type',
                  header: 'Data type',
                  cell: (item: SchemaColumn) => {
                    if (!onSchemaChange || isSchemaLocked) {
                      return <Badge color="blue">{typeLabel(item.dataType)}</Badge>;
                    }
                    const normalizedType = typeLabel(item.dataType) as DataType;
                    const selectedOption = DATA_TYPE_OPTIONS.find(o => o.value === normalizedType)
                      || DATA_TYPE_OPTIONS.find(o => o.value === item.dataType)
                      || DATA_TYPE_OPTIONS[0];
                    // Fix 2 (2026-05-28): show inline warning when the user
                    // picked decimal/integer for a column whose sampled
                    // values look like dates (≥30% match). The most common
                    // way users hit the FusionTransform NPE was by manually
                    // mistyping date columns as decimal because the legacy
                    // inferType missed M/D/YY format and labeled them
                    // string, then the user "fixed" the type to decimal.
                    const dateWarning = (item.warnings || []).find(
                      w => w.type === 'looks_like_date'
                    );
                    const showDateMismatch =
                      dateWarning && (item.dataType === 'decimal' || item.dataType === 'integer');
                    return (
                      <SpaceBetween size="xxs">
                        <Select
                          selectedOption={selectedOption}
                          onChange={({ detail }) => {
                            const updated = schema!.map(c =>
                              c.columnName === item.columnName
                                ? { ...c, dataType: detail.selectedOption.value as DataType }
                                : c
                            );
                            onSchemaChange(updated);
                          }}
                          options={DATA_TYPE_OPTIONS}
                          expandToViewport
                          ariaLabel={`Data type for ${item.columnName}`}
                        />
                        {showDateMismatch && dateWarning && (
                          <Box color="text-status-warning" fontSize="body-s">
                            ⚠ Looks like a date (
                            {Math.round((dateWarning.confidence ?? 0) * 100)}% match
                            {dateWarning.sample ? `, e.g. "${dateWarning.sample}"` : ''}). Consider <strong>timestamp</strong>.
                          </Box>
                        )}
                      </SpaceBetween>
                    );
                  },
                  minWidth: 200,
                },
                {
                  id: 'null',
                  header: (onSchemaChange && !isSchemaLocked && schema && schema.length > 0) ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      <Checkbox
                        checked={schema.every(c => c.nullable)}
                        indeterminate={schema.some(c => c.nullable) && !schema.every(c => c.nullable)}
                        onChange={({ detail }) => onSchemaChange(schema.map(c => ({ ...c, nullable: detail.checked })))}
                        ariaLabel="Set all columns nullable"
                      />
                      <span>Nullable</span>
                    </SpaceBetween>
                  ) : 'Nullable',
                  cell: (item: SchemaColumn) => {
                    if (!onSchemaChange || isSchemaLocked) {
                      return item.nullable ? 'Yes' : 'No';
                    }
                    return (
                      <Checkbox
                        checked={item.nullable}
                        onChange={({ detail }) => {
                          const updated = schema!.map(c =>
                            c.columnName === item.columnName
                              ? { ...c, nullable: detail.checked }
                              : c
                          );
                          onSchemaChange(updated);
                        }}
                        ariaLabel={`Nullable for ${item.columnName}`}
                      />
                    );
                  },
                  width: 90,
                },
                {
                  id: 'doc',
                  header: 'Description',
                  cell: (item: SchemaColumn) => {
                    if (!onSchemaChange || isSchemaLocked) {
                      return <Box color="text-body-secondary">{item.doc || ''}</Box>;
                    }
                    return (
                      <Input
                        value={item.doc || ''}
                        onChange={({ detail }) => {
                          const updated = schema!.map(c =>
                            c.columnName === item.columnName
                              ? { ...c, doc: detail.value }
                              : c
                          );
                          onSchemaChange(updated);
                        }}
                        placeholder="Column description"
                        ariaLabel={`Description for ${item.columnName}`}
                      />
                    );
                  },
                },
              ]}
              items={schema}
              variant="embedded"
            />
          </SpaceBetween>
        </ExpandableSection>
      )}

      {/* Raw Data Preview */}
      {filePreview && filePreview.length > 1 && (() => {
        const allRows = filePreview.slice(1);
        const sampleSize = Math.min(100, allRows.length);
        // Random sampling for data diversity (Fisher-Yates partial shuffle)
        const sampled = [...allRows];
        for (let i = 0; i < sampleSize && i < sampled.length; i++) {
          const j = i + Math.floor(Math.random() * (sampled.length - i));
          [sampled[i], sampled[j]] = [sampled[j], sampled[i]];
        }
        const displayRows = sampled.slice(0, sampleSize);
        return (
          <ExpandableSection headerText={`Data Preview (${sampleSize} of ${allRows.length} rows, random sample)`} variant="footer">
            <Table
              columnDefinitions={filePreview[0].map((h, i) => ({ id: `c${i}`, header: h, cell: (item: string[]) => item[i] || '' }))}
              items={displayRows}
              variant="embedded"
            />
          </ExpandableSection>
        );
      })()}
    </SpaceBetween>
  );
};

export default BasicSettingsForm;
